import { GrowPortfolioAllocation, InyovaGrowRiskSurveyQuestion, InyovaGrowSubmit } from '@inyova/grow';
import { GrowAccount } from '@inyova/models';

export const getPercentageOfPortfolio = (growAccount: GrowAccount, amountToInvest: number, projectCurrency: string): number => {
  if (growAccount.portfolio_value === 0) return 0;
  const isDifferentCurrency = projectCurrency.toLowerCase() !== growAccount.currency.toLowerCase();

  const amountInPortfolio = isDifferentCurrency ? amountToInvest * growAccount.eur_exchange_rate : amountToInvest;

  return ((amountInPortfolio / growAccount.portfolio_value) * 100) / 100;
};

export const calculatePortfolioAllocation = (total: number, invested: number, commited: number, accrued: number, cash: number) => {
  const values: Record<string, number> = {
    invested,
    commited,
    accrued,
    cash
  };

  const exactPercentages = Object.entries(values).map(([key, value]) => ({ key, value, percentage: (value / total) * 100 }));
  const rounded = exactPercentages.map((item) => ({
    key: item.key,
    percentage: Math.floor(item.percentage),
    remainder: item.percentage - Math.floor(item.percentage)
  }));

  const totalRounded = rounded.reduce((acc, item) => acc + item.percentage, 0);
  const diff = 100 - totalRounded;

  const sortedByRemainder = [...rounded].sort((a, b) => b.remainder - a.remainder);
  for (let i = 0; i < diff; i++) {
    sortedByRemainder[i].percentage += 1;
  }

  const result = Object.fromEntries(sortedByRemainder.map((item) => [item.key, item.percentage]));

  return {
    investedInProjects: { percentage: result['invested'] / 100, value: invested },
    commitedToProjects: { percentage: result['commited'] / 100, value: commited },
    accruedInterest: { percentage: result['accrued'] / 100, value: accrued },
    cash: { percentage: result['cash'] / 100, value: cash }
  } as GrowPortfolioAllocation;
};

export const getGrowInvestmentDuration = (investHorizon?: InyovaGrowRiskSurveyQuestion) => {
  const answerId = investHorizon?.survey_customer_answers?.answer_value;
  const selectedOption = typeof answerId === 'number' ? investHorizon.survey_question_options.find((option) => option.id === answerId) : undefined;
  return (
    {
      1: '0-5',
      2: '5-10',
      3: '10-20',
      4: '20+'
    }[selectedOption?.inyova_id] ?? null
  );
};

export const getGrowAgeAt = (ageQuestion?: InyovaGrowRiskSurveyQuestion) => {
  if (typeof ageQuestion === 'undefined') {
    return null;
  }
  const answerId = ageQuestion?.survey_customer_answers?.answer_value;
  const selectedOption = typeof answerId === 'number' ? ageQuestion.survey_question_options.find((option) => option.id === answerId) : undefined;
  return (
    {
      6: 24,
      7: 35,
      8: 45,
      9: 55,
      10: 60
    }[selectedOption?.inyova_id] ?? null
  );
};

export const getGrowInvestmentPercentage = (investmentPercentageQuestion?: InyovaGrowRiskSurveyQuestion) => {
  if (typeof investmentPercentageQuestion === 'undefined') {
    return null;
  }
  const answerId = investmentPercentageQuestion?.survey_customer_answers?.answer_value;
  const selectedOption =
    typeof answerId === 'number' ? investmentPercentageQuestion.survey_question_options.find((option) => option.id === answerId) : undefined;
  return (
    {
      11: '80-100',
      12: '60-80',
      13: '40-60',
      14: '20-40',
      15: '0-20'
    }[selectedOption?.inyova_id] ?? null
  );
};

export const getGrowExistingInvestments = (existingInvestmentsQuestion?: InyovaGrowRiskSurveyQuestion) => {
  if (typeof existingInvestmentsQuestion === 'undefined') {
    return null;
  }
  const answerId = existingInvestmentsQuestion?.survey_customer_answers?.answer_value;
  const selectedOption =
    typeof answerId === 'number' ? existingInvestmentsQuestion?.survey_question_options.find((option) => option.id === answerId) : undefined;
  return (
    {
      16: 'none',
      17: 'small_portion',
      18: 'more_than_half',
      19: 'less_than_half',
      20: 'not_sure'
    }[selectedOption?.inyova_id] ?? null
  );
};

export const getGrowRiskProfileSubmittedProperties = (
  data: InyovaGrowSubmit,
  recommendedRiskLevelRawValue: number,
  previousAssetAllocation: number | null,
  isAfterFirstCommitment: boolean
) => {
  const selectedAssetAllocation = data.interestAccount.projects_percentage;

  return {
    offering_account_type: 'grow',
    selected_asset_allocation: selectedAssetAllocation,
    recommended_risk_level_raw_value: recommendedRiskLevelRawValue,
    is_risk_overwritten: selectedAssetAllocation > recommendedRiskLevelRawValue,
    is_after_first_commitment: isAfterFirstCommitment,
    ...(previousAssetAllocation !== null && { previous_asset_allocation: previousAssetAllocation })
  };
};
